import { useEventConfig } from '@/eventConfig/useEventConfig';
import { useEffect, useState } from 'react';
import { useBmiCalculation } from './useBmiCalculation';
import { useLayer } from '@/layers/useLayer';
import { BmiResult } from './bmiUtil';
import { FormGetFieldValueFn } from '@/form/FormProvider';

export interface BmiValidation {
  isValidBmi: boolean;
  bmi: BmiResult | undefined;
}

// If no values are specified in bmi.yaml, the following default values
// (suitable for metric calculations using cm and kg) will be used.
export const MAX_BMI = 40;
export const MIN_BMI = 16.5;
export const CALC_THRESHOLD_HEIGHT = 80;
export const CALC_THRESHOLD_WEIGHT = 20;

export const useBmiValidation = (getFieldValue: FormGetFieldValueFn): BmiValidation => {
  const { eventConfig } = useEventConfig();
  const { showLayer } = useLayer();
  const { bmi } = useBmiCalculation(getFieldValue);
  const [isValidBmi, setIsValidBmi] = useState<boolean>(true);

  const slide = eventConfig?.slides.bmi;

  useEffect(() => {
    if (Number.isNaN(bmi?.bmi)) return setIsValidBmi(false);

    const isThresholdHeightValid = validateMinCalculationThresholdHeight();

    const isThresholdWeightValid = validateMinCalculationThresholdWeight();

    const isMinValid = validateMin();

    if (!isMinValid && isThresholdHeightValid && isThresholdWeightValid) {
      showLayer({ ref: '@bmiHint' });
      return setIsValidBmi(false);
    }

    const isMaxValid = validateMax();
    if (!isMaxValid && isThresholdHeightValid && isThresholdWeightValid) {
      showLayer({ ref: '@bmiHint' });
    }

    setIsValidBmi(isMaxValid && isMinValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bmi]);

  const validateMin = (): boolean => {
    if (bmi?.bmi == undefined) {
      return true;
    }

    return bmi.bmi >= (slide?.minBmi ?? MIN_BMI);
  };

  const validateMax = (): boolean => {
    if (bmi?.bmi == undefined) {
      return true;
    }

    return bmi?.bmi <= (slide?.maxBmi ?? MAX_BMI);
  };

  const validateMinCalculationThresholdHeight = (): boolean => {
    if (bmi?.centimeter == undefined) {
      return true;
    }

    return bmi?.centimeter >= (slide?.minCalculationThresholdHeight ?? CALC_THRESHOLD_HEIGHT);
  };

  const validateMinCalculationThresholdWeight = (): boolean => {
    if (bmi?.kilo == undefined) {
      return true;
    }

    return bmi?.kilo >= (slide?.minCalculationThresholdWeight ?? CALC_THRESHOLD_WEIGHT);
  };

  return { isValidBmi, bmi };
};
